import React, { useState } from 'react';
import { XIcon, AlertCircleIcon } from 'lucide-react';
interface RefundModalProps {
  isOpen: boolean;
  onClose: () => void;
  transactionAmount: number;
  transactionId: string;
  currency?: string;
  onRefund: (amount: number, reason: string, notes: string) => void;
}
export function RefundModal({
  isOpen,
  onClose,
  transactionAmount,
  transactionId,
  currency = 'THB',
  onRefund
}: RefundModalProps) {
  const [refundAmount, setRefundAmount] = useState('');
  const [selectedReason, setSelectedReason] = useState('');
  const [notes, setNotes] = useState('');
  const [showReasonError, setShowReasonError] = useState(false);
  const refundReasons = ['Requested by customer', 'Duplicated', 'Fraudulent', 'Abandoned', 'Others'];
  const handleRefundAll = () => {
    setRefundAmount(transactionAmount.toLocaleString('en-US'));
  };
  const handleRefund = () => {
    if (!selectedReason) {
      setShowReasonError(true);
      return;
    }
    const amount = parseFloat(refundAmount.replace(/,/g, '')) || 0;
    if (amount > 0 && amount <= transactionAmount) {
      onRefund(amount, selectedReason, notes);
      handleClose();
    }
  };
  const handleAmountChange = (value: string) => {
    // Remove all non-digit characters except decimal point
    const cleanValue = value.replace(/[^\d.]/g, '');
    // Split into integer and decimal parts
    const parts = cleanValue.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];
    // Add thousand separators to integer part
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // Combine with decimal part if it exists
    const formattedValue = decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
    setRefundAmount(formattedValue);
  };
  const handleClose = () => {
    setRefundAmount('');
    setSelectedReason('');
    setNotes('');
    setShowReasonError(false);
    onClose();
  };
  if (!isOpen) return null;
  const isRefundDisabled = !selectedReason || !refundAmount || parseFloat(refundAmount) <= 0;
  return <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity" onClick={handleClose} data-id="element-3616" />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-3617">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-id="element-3618">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-3619">
            <div data-id="element-3620">
              <h2 className="text-xl font-semibold text-gray-900" data-id="element-3621">
                Refund the customer
              </h2>
              <p className="text-sm text-gray-500 mt-1" data-id="element-3622">
                Refunds take 2-5 days to appear on your customer's statement.
              </p>
            </div>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-3623">
              <XIcon size={24} data-id="element-3624" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6" data-id="element-3625">
            {/* Refund Amount */}
            <div data-id="element-3626">
              <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-3627">
                Refund amount
              </label>
              <div className="relative" data-id="element-3628">
                <input type="text" value={refundAmount} onChange={e => handleAmountChange(e.target.value)} placeholder="0.00" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg" data-id="element-3629" />
                <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium" data-id="element-3630">
                  {currency}
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-2" data-id="element-3631">
                You can refund up to the full payment amount of{' '}
                <span className="font-medium text-gray-900" data-id="element-3632">
                  {transactionAmount.toLocaleString('en-US')} {currency}
                </span>
                .{' '}
                <button onClick={handleRefundAll} className="text-blue-600 hover:text-blue-700 font-medium" data-id="element-3633">
                  Refund all
                </button>
              </p>
            </div>

            {/* Reason */}
            <div data-id="element-3634">
              <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-3635">
                Reason
              </label>
              <select value={selectedReason} onChange={e => {
              setSelectedReason(e.target.value);
              setShowReasonError(false);
            }} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white ${showReasonError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'}`} style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%236B7280' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 1rem center'
            }} data-id="element-3636">
                <option value="" data-id="element-3637">Select a reason</option>
                {refundReasons.map(reason => <option key={reason} value={reason} data-id="element-3638">
                    {reason}
                  </option>)}
              </select>
              <p className="text-sm text-gray-500 mt-2" data-id="element-3639">
                This is only visible to you and will not be shown to the
                customer.
              </p>
              {showReasonError && <div className="flex items-center gap-2 mt-2 text-sm text-red-600" data-id="element-3640">
                  <AlertCircleIcon size={16} data-id="element-3641" />
                  <span data-id="element-3642">Required</span>
                </div>}
            </div>

            {/* Notes */}
            <div data-id="element-3643">
              <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-3644">
                Notes{' '}
                <span className="text-gray-400 font-normal" data-id="element-3645">Optional</span>
              </label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add more details about this refund..." rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none" data-id="element-3646" />
              <p className="text-sm text-gray-500 mt-2" data-id="element-3647">
                This is only visible to you and will not be shown to the
                customer.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200" data-id="element-3648">
            <button onClick={handleClose} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors" data-id="element-3649">
              Cancel
            </button>
            <button onClick={handleRefund} disabled={isRefundDisabled} className={`px-6 py-2 text-sm font-medium rounded-lg transition-colors ${isRefundDisabled ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-900 text-white hover:bg-gray-800'}`} data-id="element-3650">
              Refund
            </button>
          </div>
        </div>
      </div>
    </>;
}